import React, { ReactElement, ReactNode } from 'react';
import Skeleton from '@kiwicom/orbit-components/lib/Skeleton';
import { Props as SkeletonProps } from '@kiwicom/orbit-components/lib/Skeleton/types.d';

// {} being a non-nullish type is exactly what we want here
// eslint-disable-next-line @typescript-eslint/ban-types
type NonUndefined = {} | null;

type Props<ChildrenData> = {
  children: (data: ChildrenData) => ReactNode | null;
  data: ChildrenData | undefined;
  skeleton?: SkeletonProps;
  isLoading: boolean;
};

/**
 * Handles loading state for components which require fetched data. When `isLoading` is true,
 * loading skeleton is displayed, otherwise the children with fetched data.
 *
 * If `isLoading` is false and `data` are undefined, nothing is displayed. Use case: when API returns
 * error and page doesn't redirect to the error or login page fast enough, the component won't throw
 * an error (e.g. `Cannot read properties of undefined (reading map)`) and won't trigger Sentry.
 *
 * Orbit's Skeleton component ({@link https://orbit.kiwi/components/progress-indicators/skeleton/react/})
 * is used as a loader element. Prop `skeleton` accepts all Skeleton's props.
 * Example: `skeleton={{ width: "80px", height: 22, title: 'Loading title' }}`.
 *
 * @returns loading skeleton or children with data
 */
const Loadable = <ChildrenData extends NonUndefined>({
  children,
  data,
  skeleton,
  isLoading,
}: Props<ChildrenData>): ReactElement | null => {
  if (isLoading) {
    // TODO MMB-4683: tkey for title
    return <Skeleton title="Loading..." {...skeleton} />;
  }

  // In case loading finished but data are undefined (error happened) and
  // page hasn't redirected fast enough to the error or login page.
  if (typeof data === 'undefined') {
    return null;
  }

  return <>{children(data)}</>;
};

export default Loadable;
